import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/site/Reveal";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.25, 0.6]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100vh] flex flex-col justify-end pb-20 md:pb-24 px-6 md:px-12 overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/2414036/pexels-photo-2414036.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Abstract clinical texture"
          className="w-full h-[120%] object-cover grayscale"
        />
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone via-stone/10 to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-6xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-8 h-px bg-ink/60" />
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ink/70">
            Two brands · One standard of care
          </span>
        </motion.div>

        <h1 className="font-serif text-[15vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.82] tracking-tighter text-ink">
          <MaskedLines animate lines={["Precision", <span key="p" className="text-pharma">Pharma.</span>]} />
          <MaskedLines animate stagger={0.12} lines={["Dermatological", <span key="d" className="text-derma">Beauty.</span>]} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 max-w-xl font-sans text-base md:text-lg text-ink/70 leading-relaxed"
        >
          OneKind Healthcare unites clinically-formulated oral medications & wellness with
          dermatologist-grade skincare — trusted by the doctors who prescribe them.
        </motion.p>
      </motion.div>

      <motion.a
        href="#onekind"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 right-6 md:right-12 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60 hover:text-pharma transition-colors"
      >
        Scroll
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.a>
    </section>
  );
}
